import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

//MUI icons
import IconSearch from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";

//custom icons
import { IconFolderSearch, IconGroupSearch } from "../../../Icons/icons";

//custom components
import Logo from "../Logo/Logo.component";
import FilePreview from "../../../components/common/FilePreview/FilePreview.component";

//hooks
import { useAuth } from "../../../hooks/hooks";

//styles
import { bgSections, inputBorder, bgForms, linkColor } from "../../../styles/theme";

//utils
import axios from "../../../utils/api";

const HeaderComponent = styled.header`
  width: 100%;
  top: 0;
  left: 0;
  position: fixed;
  height: calc(12.8 * var(--atom));
  background: ${bgSections};
  box-shadow: 0px 2.59488px 7.78464px rgba(0, 0, 0, 0.15);
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(2.4 * var(--atom));

  .logo-wrapper {
    max-width: calc(32 * var(--atom));
    display: flex;
    align-items: center;
    cursor: pointer;
    padding-right: calc(2 * var(--atom));

    svg {
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    flex-direction: column;
    height: calc(25.8 * var(--atom));
    .logo-wrapper {
      padding: 0;
      margin-bottom: calc(2 * var(--atom));
    }
  }

  .Mui-focused {
    fieldset {
      border: 1px solid ${linkColor} !important;
    }
  }

  .right-content-wrapper {
    display: flex;
    .search-field {
      width: calc(38 * var(--atom));
      height: calc(6.4 * var(--atom));
      border: 1px solid ${inputBorder};
      border-radius: calc(0.8 * var(--atom));
      padding: 0 calc(2 * var(--atom));
      display: flex;
      align-items: center;
      span {
        font-size: calc(2 * var(--atom));
        font-style: italic;
        margin-left: var(--atom);
      }
    }

    .lang-field {
      display: flex;
      align-items: center;
      justify-content: center;
      padding-left: calc(2 * var(--atom));

      .lang {
        // width: calc(8.4 * var(--atom));
        // height: calc(5.1 * var(--atom));
        width: 42px;
        height: 25px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
        padding: 5px;
        margin: 0 calc (0.8 * var(--atom));
        background: ${bgForms};
        border: 1px solid ${bgForms};
        cursor: pointer;
        overflow: hidden;
        margin-right: 5px;

        &:last-of-type {
          margin-right: 0;
        }

        span {
          max-width: 100%;
          max-height: 100%;
          display: flex !important;
          align-items: center;
        }

        img {
          width: 100%;
          height: 100%;
          // max-height: calc(5 * var(--atom));
        }

        &.active {
          border: 1px solid ${linkColor};
        }
      }
    }
  }
`;

const SearchInputWrapper = styled.div`
  width: calc(70 * var(--atom));
  display: flex;
  align-items: center;
  position: relative;

  .icon-search {
    position: absolute;
    z-index: 2;
    left: calc(2 * var(--atom));
    top: calc(2 * var(--atom));
  }

  input {
    padding-left: calc(7 * var(--atom)) !important;
  }
  
  @media (max-width: 480px) {
    width: calc(50 * var(--atom));
  }
`;

const AutocompleteItem = styled.li`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${inputBorder};
  padding-bottom: calc(2 * var(--atom));

  &:last-of-type {
    border: none;
  }

  li {
    width: 100%;
    padding-left: calc(14.2 * var(--atom)) !important;
  }
`;

const GroupHeader = styled.div`
  padding: 0 calc(2.4 * var(--atom));
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: calc(4 * var(--atom));
  line-height: calc(5.4 * var(--atom));
  .icon-wrapper {
    width: calc(10 * var(--atom));
    height: calc(10 * var(--atom));
    margin-right: calc(2 * var(--atom));
    display: flex;
    align-items: center;
    justify-content: center;

    > svg {
      max-width: 100%;
      max-height: 100%;
    }
  }
`;

const ChildWrapper = styled.div`
  padding-left: cacl(8 * var(--atom));
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface Props {}

let timerID: any;

const Header: React.FC<Props> = () => {
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<any[]>([]);
  const [file, setFile] = useState<any>(null);

  const { languages } = useAuth();
  const { i18n, t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const renderGroupHeader = (groupType: string) => {
    switch (groupType) {
      case "folder":
        return (
          <>
            <div className="icon-wrapper">
              <IconFolderSearch />
            </div>{" "}
            {t("common.folders")}
          </>
        );
      case "category":
        return (
          <>
            <div className="icon-wrapper">
              <IconFolderSearch />
            </div>{" "}
            {t("common.categories")}
          </>
        );
      case "file":
        return (
          <>
            <div className="icon-wrapper">
              <IconGroupSearch />
            </div>{" "}
            {t("common.groups")}
          </>
        );
      default:
        return null;
    }
  };

  const handleSearch = (event: any) => {
    clearTimeout(timerID);

    if (event?.target?.value && event?.target?.value?.length > 2) {
      timerID = setTimeout(() => searchItems(event?.target?.value), 500);
    } else {
      setOptions([]);
    }
  };

  const searchItems = async (value: string) => {
    try {
      setSearchLoading(true);
      const res = await axios.get(`search?filter[search]=${value}`, {
        headers: {
          "Accept-Language": i18n.language,
        },
      });
      setOptions(
        Array.isArray(res.data?.data)
          ? res.data?.data.map((el: any) => ({
              ...el,
              label: el.name,
            }))
          : []
      );
    } catch (e) {
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    setFile(null);
  }, [pathname]);

  return (
    <HeaderComponent>
      <div
        className="logo-wrapper"
        onClick={() => navigate("/")}
      >
        <Logo />
      </div>
      <div className="right-content-wrapper">
        <SearchInputWrapper>
          <IconSearch className="icon-search" />
          <Autocomplete
            loading={searchLoading}
            disablePortal
            onInput={handleSearch}
            onChange={(event: any, value: any) => {
              if (value && typeof value !== "string") {
                if (value?.type === "file") {
                  navigate(`/file/${value.id}`);
                } else {
                  navigate(
                    value?.category_id
                      ? `/categories/${value.category_id}/${value.id}`
                      : `/categories/${value.id}`
                  );
                }
              }
            }}
            options={options}
            freeSolo
            sx={{ width: "100%" }}
            groupBy={(option: { label: string; type: string }) => option?.type}
            renderGroup={(params) => {
              return (
                <AutocompleteItem key={params.key}>
                  <GroupHeader>{renderGroupHeader(`${params.group}`)}</GroupHeader>
                  <ChildWrapper>{params.children}</ChildWrapper>
                </AutocompleteItem>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{
                  shrink: false,
                }}
                placeholder={`${t("form.labels.search")}`}
                className="with-start-icon"
                size="small"
                sx={{ border: 0 }}
              />
            )}
          />
        </SearchInputWrapper>
        <div className="lang-field">
          {languages.map((lang) => (
            <div
              key={lang.code}
              className={`lang${i18n.language === lang.code ? " active" : ""}`}
              onClick={() => i18n.changeLanguage(lang.code)}
            >
              {t(`common.${lang.code}`)}

              {/* <LazyLoadImage
                alt={lang.code}
                src={lang.icon}
                effect="opacity"
              /> */}
            </div>
          ))}
        </div>
      </div>
      {file && (
        <FilePreview
          file={file}
          closeFn={() => setFile(null)}
          groupTitle={file.name}
        />
      )}
    </HeaderComponent>
  );
};

export default Header;
